import type { ErrorInfo, PropsWithChildren, ReactNode } from 'react';
import { Component } from 'react';

type ErrorBoundaryProps = {
  fallback?: ReactNode;
  isGlobal?: boolean;
  showError?: boolean;
  onPromiseRejectionCallback?: (event: PromiseRejectionEvent) => void;
  onGlobalErrorCallback?: (event: ErrorEvent) => void;
  onErrorCallback?: (event: Error, errorInfo: ErrorInfo) => void;
};

type ErrorBoundaryState = {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
};

class ErrorBoundary extends Component<PropsWithChildren<ErrorBoundaryProps>, ErrorBoundaryState> {
  private readonly showError: boolean = false;
  private readonly isGlobal: boolean = false;
  private readonly fallback: ReactNode = (<h2>Something went wrong.</h2>);
  private readonly onPromiseRejectionCallback: undefined | ((event: PromiseRejectionEvent) => void) = undefined;
  private readonly onGlobalErrorCallback: undefined | ((event: ErrorEvent) => void) = undefined;
  private readonly onErrorCallback: undefined | ((event: Error, errorInfo: ErrorInfo) => void) = undefined;

  constructor(props: PropsWithChildren<ErrorBoundaryProps>) {
    super(props);
    if (props?.fallback) this.fallback = props.fallback;
    if (props?.showError) this.showError = props.showError;
    if (props?.isGlobal) this.isGlobal = props.isGlobal;
    if (props?.onPromiseRejectionCallback) this.onPromiseRejectionCallback = props.onPromiseRejectionCallback;
    if (props?.onGlobalErrorCallback) this.onGlobalErrorCallback = props.onGlobalErrorCallback;
    if (props?.onErrorCallback) this.onErrorCallback = props.onErrorCallback;

    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidMount() {
    if (this.isGlobal) {
      window.addEventListener('error', this.handleGlobalError);
      window.addEventListener('unhandledrejection', this.handlePromiseRejection);
    }
  }

  componentWillUnmount() {
    if (this.isGlobal) {
      window.removeEventListener('error', this.handleGlobalError);
      window.removeEventListener('unhandledrejection', this.handlePromiseRejection);
    }
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ error, errorInfo });
    console.error('Caught an error:', error, errorInfo);
    if (this.onErrorCallback) this.onErrorCallback(error, errorInfo);
  }

  handleGlobalError = (event: ErrorEvent) => {
    console.error('Global error caught:', event.error);
    this.setState({ hasError: true, error: event.error });
    if (this.onGlobalErrorCallback) this.onGlobalErrorCallback(event);
  };

  handlePromiseRejection = (event: PromiseRejectionEvent) => {
    console.error('Unhandled promise rejection:', event.reason);
    this.setState({ hasError: true, error: event.reason });
    if (this.onPromiseRejectionCallback) this.onPromiseRejectionCallback(event);
  };

  render() {
    if (this.state.hasError && !this.isGlobal) {
      return (
        <div>
          {this.fallback}
          {this.state.error && this.showError && <p>{this.state.error.toString()}</p>}
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
